import { useRef, useState } from "react";
import { toast } from "sonner";
import { Type, Heading, Braces, Image as ImageIcon, UserSquare, Square, Circle, Minus, QrCode, Gem, Layers, PlusSquare, Upload, Loader2, Stamp, Award, PenLine, Frame, Barcode } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { makeElement, makeJewellery, COMMON_FIELDS, JEWELLERY_SPECS } from "@/lib/card";
import { uploadImage, errMsg } from "@/lib/api";
import LayersPanel from "./LayersPanel";
import BrandTools from "./BrandTools";

const Tool = ({ icon: Icon, label, onClick, testId, gold }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex flex-col items-center justify-center gap-1.5 h-[72px] rounded-lg border bg-slate-950/60 text-[11px] font-medium transition-colors ${gold ? "border-amber-900/60 hover:border-amber-500/70 text-amber-100" : "border-slate-800 hover:border-slate-600 text-slate-300 hover:text-white"}`}>
    <Icon size={18} className={gold ? "text-amber-400" : ""} />
    {label}
  </button>
);

const Section = ({ title, children }) => (
  <div className="space-y-2.5">
    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{title}</p>
    {children}
  </div>
);

function ElementsTab({ tpl, addEls }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [custom, setCustom] = useState("");
  const add = (kind, extra) => addEls([makeElement(kind, tpl, extra)]);
  const addField = (key) => {
    const k = key.trim().toUpperCase().replace(/[^A-Z0-9_]/g, "_");
    if (k) add("field", { key: k });
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { add("image", { src: await uploadImage(f) }); } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-6">
      <Section title="Text">
        <div className="grid grid-cols-3 gap-2">
          <Tool icon={Heading} label="Heading" onClick={() => add("heading")} testId="tool-add-heading" />
          <Tool icon={Type} label="Text" onClick={() => add("text")} testId="tool-add-text" />
          <Tool icon={QrCode} label="QR code" onClick={() => add("qr")} testId="tool-add-qr" />
          <Tool icon={Barcode} label="Barcode" onClick={() => add("barcode")} testId="tool-add-barcode" />
        </div>
      </Section>
      <Section title="Images">
        <div className="grid grid-cols-3 gap-2">
          <Tool icon={uploading ? Loader2 : Upload} label="Logo / image" onClick={() => fileRef.current?.click()} testId="tool-add-image" />
          <Tool icon={UserSquare} label="Photo field" onClick={() => add("photo")} testId="tool-add-photo-field" />
          <Tool icon={ImageIcon} label="Image slot" onClick={() => add("image")} testId="tool-add-image-slot" />
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="tool-image-file-input" />
      </Section>
      <Section title="Shapes">
        <div className="grid grid-cols-3 gap-2">
          <Tool icon={Square} label="Rectangle" onClick={() => add("rect")} testId="tool-add-shape" />
          <Tool icon={Circle} label="Circle" onClick={() => add("ellipse")} testId="tool-add-ellipse" />
          <Tool icon={Minus} label="Line" onClick={() => add("line")} testId="tool-add-line" />
        </div>
      </Section>
      <Section title="Your store brand">
        <BrandTools tpl={tpl} addEls={addEls} />
      </Section>
      <Section title="Data fields — merged from records">
        <div className="flex flex-wrap gap-1.5">
          {COMMON_FIELDS.map((f) => (
            <button key={f} onClick={() => addField(f)} data-testid={`tool-field-${f}`}
              className="px-2 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-200 text-[10px] font-mono hover:bg-indigo-500/20 transition-colors">
              {`{{${f}}}`}
            </button>
          ))}
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); addField(custom); setCustom(""); }}>
          <input className="cf-input h-8 text-xs font-mono" placeholder="CUSTOM_FIELD" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="custom-field-input" />
          <button className="cf-btn-ghost h-8 text-xs shrink-0" data-testid="custom-field-add-button"><Braces size={13} /> Add</button>
        </form>
      </Section>
    </div>
  );
}

function JewelleryTab({ tpl, addEls }) {
  const [specY, setSpecY] = useState(15);
  const add = (kind, opts) => addEls(makeJewellery(kind, tpl, opts));
  const addSpec = (s) => { add("spec", { ...s, y: specY }); setSpecY((y) => (y + 4 > tpl.height - 6 ? 15 : y + 4)); };
  return (
    <div className="space-y-6">
      <Section title="Certificate elements">
        <div className="grid grid-cols-3 gap-2">
          <Tool gold icon={Award} label="Title" onClick={() => add("title")} testId="jewel-add-title" />
          <Tool gold icon={Frame} label="Gold border" onClick={() => add("border")} testId="jewel-add-border" />
          <Tool gold icon={Stamp} label="Hallmark" onClick={() => add("hallmark")} testId="jewel-add-hallmark" />
          <Tool gold icon={Gem} label="Item photo" onClick={() => add("photo")} testId="jewel-add-photo" />
          <Tool gold icon={QrCode} label="Verify QR" onClick={() => add("qr")} testId="jewel-add-qr" />
          <Tool gold icon={Award} label="Genuine" onClick={() => add("badge")} testId="jewel-add-badge" />
          <Tool gold icon={PenLine} label="Signature" onClick={() => add("signature")} testId="jewel-add-signature" />
        </div>
      </Section>
      <Section title="Specification rows (label + value)">
        <div className="grid grid-cols-2 gap-1.5">
          {JEWELLERY_SPECS.map((s) => (
            <button key={s.key} onClick={() => addSpec(s)} data-testid={`jewel-spec-${s.key}`}
              className="text-left px-2.5 py-2 rounded-md border border-amber-900/50 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
              <span className="block text-[11px] text-amber-100 font-semibold">{s.label}</span>
              <span className="block text-[9px] font-mono text-amber-400/70">{`{{${s.key}}}`}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">Rows stack automatically. Tip: start from the “Jewellery Certificate” template on the dashboard for a ready layout.</p>
      </Section>
    </div>
  );
}

export default function Toolbox(props) {
  return (
    <aside className="w-72 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col min-h-0" data-testid="toolbox">
      <Tabs defaultValue="elements" className="flex flex-col min-h-0 flex-1">
        <TabsList className="m-3 mb-0 grid grid-cols-3 bg-slate-950 border border-slate-800 h-9">
          <TabsTrigger value="elements" data-testid="tool-tab-elements" className="text-xs gap-1"><PlusSquare size={13} />Add</TabsTrigger>
          <TabsTrigger value="jewellery" data-testid="tool-tab-jewellery" className="text-xs gap-1"><Gem size={13} />Jewel</TabsTrigger>
          <TabsTrigger value="layers" data-testid="tool-tab-layers" className="text-xs gap-1"><Layers size={13} />Layers</TabsTrigger>
        </TabsList>
        <div className="flex-1 overflow-y-auto p-4">
          <TabsContent value="elements" className="mt-0"><ElementsTab {...props} /></TabsContent>
          <TabsContent value="jewellery" className="mt-0"><JewelleryTab {...props} /></TabsContent>
          <TabsContent value="layers" className="mt-0"><LayersPanel {...props} /></TabsContent>
        </div>
      </Tabs>
    </aside>
  );
}
